import axios, { AxiosError } from "axios"
import { API } from "../api/constants"

export type TUnauthorizedReason = "session-expired"

let unauthorizedHandler: null | ((reason: TUnauthorizedReason) => void) = null

/** Wird vom AuthContext gesetzt: meldet ab, wenn die Portal-Session abgelaufen ist. */
export const setUnauthorizedHandler = (handler: null | ((reason: TUnauthorizedReason) => void)) => {
    unauthorizedHandler = handler
}

let noCustomerHandler: null | (() => void) = null

/** Wird vom AuthContext gesetzt: Kunden-Login ohne Portal-Zugangsdatensatz (403 "No customer found for this login"). */
export const setNoCustomerHandler = (handler: null | (() => void)) => {
    noCustomerHandler = handler
}

// Session abgelaufen (bis zur nächsten Anmeldung): Fehler-Toasts werden dann unterdrückt, siehe service/messages.tsx
let sessionExpired = false
export const isSessionExpired = () => sessionExpired
export const markSessionExpired = () => {
    sessionExpired = true
}
export const resetSessionExpired = () => {
    sessionExpired = false
}

/** Fehler eines Requests, der wegen abgelaufener Session scheiterte. */
export type TSessionAwareError = AxiosError & { sessionExpired?: boolean }

// Schreibweise so im Backend (auth-contract.md, Portal-Fachroute ohne Zugangsdatensatz)
const NO_CUSTOMER_MESSAGE = "No customer found for this login"

/** Client ohne 401-Interceptor: Session-Nachprüfung, öffentliche Endpunkte (Logo/Farbe, Passwort-Seiten). */
export const sessionCheckClient = axios.create({
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
})

/** Alle Fach-Requests: das Portal-Session-Cookie wird per `withCredentials` mitgeschickt. */
export const authenticatedAxiosInstance = axios.create({
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true,
})

// Anmelde-Endpunkte: ein 401 dort ist eine normale Antwort (falsches Passwort, keine Session) und löst keine Nachprüfung aus.
const AUTH_ENDPOINT = /\/portal\/auth\/(session|login|logout|password)(\/|\?|$)/
const isAuthEndpoint = (url?: string) => !!url && AUTH_ENDPOINT.test(url)

// Dedupliziert parallele Nachprüfungen: viele gleichzeitige 401 -> genau ein GET /portal/auth/session und höchstens ein Logout.
let recheckInFlight: Promise<void> | null = null

export const recheckSessionOrLogout = (): Promise<void> => {
    if (!recheckInFlight) {
        recheckInFlight = sessionCheckClient
            .get(`${API}/portal/auth/session`)
            .then(() => undefined)
            .catch((error: AxiosError) => {
                // Nur wenn auch /portal/auth/session 401 liefert, ist die Session wirklich weg (Netzwerkfehler -> nicht abmelden).
                if (error?.response?.status === 401) {
                    markSessionExpired()
                    unauthorizedHandler?.("session-expired")
                }
            })
            .finally(() => {
                recheckInFlight = null
            })
    }
    return recheckInFlight
}

authenticatedAxiosInstance.interceptors.response.use(
    (response) => response,
    async (error: TSessionAwareError) => {
        const status = error?.response?.status
        if (status === 401 && !isAuthEndpoint(error?.config?.url)) {
            await recheckSessionOrLogout()
            if (sessionExpired) error.sessionExpired = true
        }
        if (status === 403 && (error?.response?.data as { message?: string } | undefined)?.message === NO_CUSTOMER_MESSAGE) {
            noCustomerHandler?.()
        }
        return Promise.reject(error)
    }
)
